import React, { useState, useMemo, useEffect } from "react";
import { ArrowLeft, MessageCircle, Plus } from "lucide-react";
import { useEtatGlobal } from "../EtatGlobal.js";
import useDiscussion from "./useDiscussion";
import ThreadList from "./organisms/ThreadList";
import ConversationPane from "./organisms/ConversationPane";
import ConversationPaneSkeleton from "./organisms/ConversationPaneSkeleton";
import NewConversationModal from "./organisms/NewConversationModal";
import { INK, INK_2, INK_3, LINE, BAD_INK, BURGUNDY, BURGUNDY_TINT } from "../Bureau/bureauTokens";

// Page "Discussion" — messagerie interne 1-à-1 entre collaborateurs (Bureau
// → raccourci "Discussion"). Layout liste + fil repris de Mails.jsx ; langage
// visuel des bulles inspiré de la maquette messagerie.jsx (SMS client), mais
// modèle de données et logique propres (collaborateurs, pas clients).
function Discussion({ onBack, initialConversationId, onInitialConversationConsumed }) {
  const { users, utilisateur } = useEtatGlobal();
  const {
    conversations,
    loadingConversations,
    selectedId,
    messages,
    loadingMessages,
    hasMoreMessages,
    error,
    selectConversation,
    loadOlderMessages,
    sendMessage,
    openConversation,
  } = useDiscussion(utilisateur?.id);

  const [showNewConversation, setShowNewConversation] = useState(false);

  // Présélection venant du drawer de notifications (Home.js/Bell) — pas de
  // ref de garde nécessaire : le parent remet initialConversationId à null
  // juste après, donc l'effet ressort au premier `if` au rendu suivant.
  useEffect(() => {
    if (!initialConversationId) return;
    selectConversation(initialConversationId);
    onInitialConversationConsumed?.();
  }, [initialConversationId, selectConversation, onInitialConversationConsumed]);

  const usersById = useMemo(() => Object.fromEntries((users || []).map((u) => [u.id, u])), [users]);

  const selectedConversation = conversations.find((c) => c.id === selectedId);
  const otherUser = selectedConversation ? usersById[selectedConversation.otherUserId] : null;

  const handleSelectUser = async (otherUserId) => {
    setShowNewConversation(false);
    await openConversation(otherUserId);
  };

  return (
    <div className="max-w-[1400px] mx-auto flex flex-col" style={{ height: "calc(100vh - 120px)" }}>
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-xs mb-3 flex-shrink-0 self-start"
          style={{ color: INK_3 }}
        >
          <ArrowLeft size={14} /> Retour au Bureau
        </button>
      )}
      <div className="flex items-center gap-2 mb-5 flex-shrink-0">
        <MessageCircle size={18} style={{ color: INK }} />
        <h1 className="text-xl font-semibold m-0" style={{ color: INK }}>
          Discussion
        </h1>
      </div>

      {error && (
        <p className="text-xs mb-3 flex-shrink-0" style={{ color: BAD_INK }}>
          {error}
        </p>
      )}

      <div className="grid gap-4 flex-1 min-h-0" style={{ gridTemplateColumns: "minmax(280px, 340px) 1fr" }}>
        <ThreadList
          conversations={conversations}
          usersById={usersById}
          selectedId={selectedId}
          onSelect={selectConversation}
          onNewConversation={() => setShowNewConversation(true)}
          loading={loadingConversations}
        />
        {!selectedId ? (
          <div
            className="h-full flex flex-col items-center justify-center gap-3 text-center px-6"
            style={{ background: "#fff", border: `1px solid ${LINE}`, borderRadius: 14 }}
          >
            <span
              className="flex items-center justify-center flex-shrink-0"
              style={{ width: 64, height: 64, borderRadius: "50%", background: BURGUNDY_TINT, color: BURGUNDY }}
            >
              <MessageCircle size={28} />
            </span>
            <div>
              <p className="text-sm font-semibold" style={{ color: INK }}>
                {conversations.length > 0 ? "Sélectionnez une conversation" : "Bienvenue dans Discussion"}
              </p>
              <p className="text-xs mt-1" style={{ color: INK_2, maxWidth: 280 }}>
                {conversations.length > 0
                  ? "Choisissez une conversation dans la liste à gauche pour l'ouvrir."
                  : "Envoyez un message direct à n'importe quel collègue du cabinet."}
              </p>
            </div>
            <button
              onClick={() => setShowNewConversation(true)}
              className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white"
              style={{ background: BURGUNDY, borderRadius: 10 }}
            >
              <Plus size={13} /> Nouveau message
            </button>
          </div>
        ) : loadingMessages && messages.length === 0 ? (
          <ConversationPaneSkeleton />
        ) : (
          <ConversationPane
            otherUser={otherUser}
            messages={messages}
            currentUserId={utilisateur?.id}
            hasMore={hasMoreMessages}
            onLoadOlder={loadOlderMessages}
            onSend={sendMessage}
          />
        )}
      </div>

      {showNewConversation && (
        <NewConversationModal
          users={users}
          currentUserId={utilisateur?.id}
          onClose={() => setShowNewConversation(false)}
          onSelect={handleSelectUser}
        />
      )}
    </div>
  );
}

export default Discussion;
